import React, { useRef } from "react";
import { Plus } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { gsap, useGsap } from "@/lib/motion";

/* Short, plain answers to what customers ask most. Rendered as real content
   (native <details>, works without JS) and mirrored as FAQPage structured data
   in HomePage, so search and answer engines can quote them directly. */
export default function QuickAnswers() {
  const { t, lang } = useLang();
  const a = t.answers;
  const root = useRef(null);

  useGsap(
    () => {
      gsap.from(".qa-item", {
        y: 24,
        opacity: 0,
        duration: 0.8,
        stagger: 0.07,
        scrollTrigger: { trigger: root.current, start: "top 78%", once: true },
      });
    },
    [lang],
    root,
  );

  return (
    <section ref={root} data-testid="answers-section" aria-labelledby="answers-title" className="bg-[var(--bg)] text-[var(--ink)] py-24 lg:py-32">
      <div className="container-x grid lg:grid-cols-12 gap-10">
        <h2 id="answers-title" className="lg:col-span-4 type-display text-[clamp(2rem,3.6vw,3rem)] max-w-[10ch]">
          {a.title}
        </h2>
        <div className="lg:col-span-8 border-t border-[var(--line)]">
          {a.items.map((item) => (
            <details key={item.q} className="qa-item group border-b border-[var(--line)]">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-lg sm:text-xl font-bold [&::-webkit-details-marker]:hidden">
                <span>{item.q}</span>
                <span className="shrink-0 w-10 h-10 rounded-full grid place-items-center bg-[var(--surface)] text-[var(--accent-ink)] transition-transform duration-500 ease-out-expo group-open:rotate-45">
                  <Plus className="w-5 h-5" aria-hidden />
                </span>
              </summary>
              <p className="qa-answer pb-7 -mt-1 text-[17px] leading-relaxed text-[var(--muted-ink)] max-w-[62ch]">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
